import useHasAccess from "@hooks/useHasAccess"

import { findCoveringSubscription } from "@utils/catalogSubscriptions"
import { faIcon } from "@utils/faIcon"

import { Table } from "@organisms/index"

import {
    useCatalogSubscriptionsQuery,
    useSubscribeMutation,
} from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import { SubscribeItemCellProps } from "./SubscribeItemCell.types"

export default function SubscribeItemCell(props: SubscribeItemCellProps) {
    const { row, catalogId } = props

    const { hasPermission } = useHasAccess()
    const hasPricebookEditPermission = hasPermission("pricebook_edit_permission", PERMISSION_LEVEL.FULL)

    const subscriptionsQuery = useCatalogSubscriptionsQuery(catalogId)
    const subscribeMutation = useSubscribeMutation()

    const subscriptions = subscriptionsQuery.data ?? []
    // Coverage is derived off the shared subscriptions listing rather than baked onto the row, so a
    // subscription change elsewhere reflects here as soon as that cache invalidates.
    const coveringSubscription = findCoveringSubscription(subscriptions, {
        id: row.id,
        brand: row.brand,
        categories: row.categories,
    })

    // Hold the cell blank until coverage is known so a covered item never flashes an unsubscribed state.
    if (subscriptionsQuery.isLoading) {
        return null
    }

    if (coveringSubscription !== null) {
        // Unsubscribing needs the coverage context a row cannot show — which subscription owns the
        // item and how broad it is — so that action lives on the item details page.
        return <Table.Cells.BadgesCell badges={["Subscribed"]} />
    }

    if (!hasPricebookEditPermission) {
        return null
    }

    const handleSubscribe = () => {
        subscribeMutation.mutate({ catalog: catalogId, level: "item", catalog_item: row.id })
    }

    return (
        <Table.Cells.ButtonCell
            variant="subtle"
            leftIcon={faIcon({ name: "circle-plus", style: "regular" })}
            onClick={handleSubscribe}
            isProcessing={subscribeMutation.isPending}
            isDisabled={subscribeMutation.isPending}
        >
            Subscribe
        </Table.Cells.ButtonCell>
    )
}
